import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Instagram, ShoppingBag, Menu, X, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { scrollToId } from "../lib/format";

const NAV = [
  { label: "Inicio", id: "inicio" },
  { label: "Catálogo", id: "catalogo" },
  { label: "Ofertas", id: "ofertas" },
  { label: "Próximos Lanzamientos", id: "lanzamientos" },
  { label: "Eventos", id: "eventos" },
  { label: "Contacto", id: "contacto" },
];

export default function Header({ settings }) {
  const { count, setOpen } = useCart();
  const { count: wishCount, setOpen: setWishOpen } = useWishlist();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenuOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-cream/85 backdrop-blur-xl border-b border-line py-2"
            : "bg-transparent py-3"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between gap-4">
          {/* Left nav (desktop) */}
          <nav className="hidden lg:flex items-center gap-7 flex-1">
            {NAV.slice(0, 3).map((n) => (
              <button
                key={n.id}
                data-testid={`nav-${n.id}`}
                onClick={() => go(n.id)}
                className="text-[11px] uppercase tracking-[0.2em] text-espresso/80 hover:text-rose transition-colors duration-300"
              >
                {n.label}
              </button>
            ))}
          </nav>

          {/* Logo */}
          <button
            data-testid="logo-home"
            onClick={() => go("inicio")}
            className="flex flex-col items-center leading-none select-none"
          >
            <span
              className={`font-display tracking-[0.15em] text-espresso transition-all duration-500 ${
                scrolled ? "text-4xl md:text-5xl" : "text-5xl md:text-6xl"
              }`}
            >
              MO
            </span>
            <span
              className={`font-serif italic text-rose -mt-1 tracking-widest transition-all duration-500 ${
                scrolled ? "text-base md:text-lg" : "text-lg md:text-xl"
              }`}
            >
              by Vir
            </span>
          </button>

          {/* Right nav + actions */}
          <div className="flex items-center gap-6 flex-1 justify-end">
            <nav className="hidden lg:flex items-center gap-7">
              {NAV.slice(3).map((n) => (
                <button
                  key={n.id}
                  data-testid={`nav-${n.id}`}
                  onClick={() => go(n.id)}
                  className="text-[11px] uppercase tracking-[0.2em] text-espresso/80 hover:text-rose transition-colors duration-300"
                >
                  {n.label}
                </button>
              ))}
            </nav>

            <a
              data-testid="header-instagram"
              href={settings?.instagram_url || "#"}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:block text-espresso hover:text-rose transition-colors"
              aria-label="Instagram"
            >
              <Instagram size={19} strokeWidth={1.5} />
            </a>

            <button
              data-testid="header-wishlist-btn"
              onClick={() => setWishOpen(true)}
              className="relative text-espresso hover:text-rose transition-colors"
              aria-label="Lista de deseos"
            >
              <Heart size={19} strokeWidth={1.5} />
              {wishCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-rose text-cream text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">
                  {wishCount}
                </span>
              )}
            </button>

            <button
              data-testid="header-cart-btn"
              onClick={() => setOpen(true)}
              className="relative text-espresso hover:text-rose transition-colors"
              aria-label="Carrito"
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-rose text-cream text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>

            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden text-espresso"
              onClick={() => setMenuOpen(true)}
              aria-label="Menú"
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-cream lg:hidden flex flex-col"
          >
            <div className="flex justify-between items-center px-5 py-6 border-b border-line">
              <span className="font-display text-2xl tracking-widest">MO by Vir</span>
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="flex flex-col px-6 py-8 gap-6">
              {NAV.map((n, i) => (
                <motion.button
                  key={n.id}
                  data-testid={`mobile-nav-${n.id}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(n.id)}
                  className="text-left font-serif text-3xl text-espresso"
                >
                  {n.label}
                </motion.button>
              ))}
              <a
                href={settings?.instagram_url || "#"}
                target="_blank"
                rel="noreferrer"
                className="mt-4 flex items-center gap-2 text-taupe text-sm uppercase tracking-widest"
              >
                <Instagram size={18} /> @Mo_ByVir
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
